import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProgressBar from "../ProgressBar";
import { BackIcon } from "../Icons";
import { generateTimeOptions } from "../GenerateTimeOptions";
import { generateWorkoutOptions } from "../GenerateWorkoutOptions";
import LanguageContext from "../../../context/LanguageContext";
import "../style.css";
import ContactButton from "../ContactButton.js";
import CustomFooter from "../CustomFooter.js";

// Import slide components
import SlideGenerateWorkout from "./SlideGenerateWorkout";
import SlideSex from "./SlideSex";
import SlideAge from "./SlideAge";
import SlideGoal from "./SlideGoal";
import SlideExperience from "./SlideExperience";
import SlideWorkout from "./SlideWorkout";
import SlideTime from "./SlideTime";
import SlideIntensity from "./SlideIntensity";
import SlideResults from "./SlideResults";
import PromoBanner from "../PromoBanner";

// Import data
const jsonDataEn = require("../../data/data-en.json");
const jsonDataFr = require("../../data/data-fr.json");

// Import the ContactButton component

const Form = ({ banner, footer, gymName, gymID, button }) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data : jsonDataFr.data;
  const [isSliding, setIsSliding] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    sex: null,
    ageRange: null,
    fitnessGoal: null,
    experience: null,
    workout: null,
    time: null,
    intensity: null,
  });

  // Listen for Bootstrap’s end-of-animation event
  useEffect(() => {
    const carousel = document.getElementById("hero-carousel");
    const onSlid = () => setIsSliding(false);

    carousel.addEventListener("slid.bs.carousel", onSlid);
    return () => carousel.removeEventListener("slid.bs.carousel", onSlid);
  }, []);

  // Handler to move between slides
  const handleSlideChange = (newStep) => {
    setCurrentStep(newStep);
  };

  // Generalized handler for form field updates
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onGenerateWorkout = async (e) => {
    e.preventDefault();
    try {
      setIsGenerating(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE}/gyms/${gymID}/workout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch: " + response.statusText);
      }

      const responseData = await response.json();
      // You probably want to reflect the new URL structure here too:
      navigate(`/${gymName}workout`, {
        state: { rawExercises: responseData, formData },
      });
    } catch (error) {
      console.error(error.message);
      if (isMountedRef.current) setIsGenerating(false);
    }
  };

  const timeOptions = generateTimeOptions(formData.workout);
  const workoutOptions = generateWorkoutOptions(formData.fitnessGoal, language);

  return (
    <section className="hero-section">
      {/* Full-screen loading overlay while generating */}
      {isGenerating && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          aria-live="polite"
          aria-busy="true"
          role="alert"
        >
          <div className="loader">
            <div className="loading-text">
              Loading<span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
          </div>
        </div>
      )}
      {/* Render ContactButton in a fixed container */}
      <div className="contact-button-fixed">{button}</div>
      <div className="footer-fixed">
        <CustomFooter line1={footer} line2={data.footer} />
      </div>

      <div className="container hero-content">
        {currentStep === 0 && <PromoBanner banner={banner} />}
        <div id="hero-carousel" className="carousel slide carousel-content" data-bs-ride="carousel" data-bs-interval="false" data-bs-touch="false">
          <ProgressBar currentStep={currentStep - 1} />
          <div className="carousel-inner">
            {/* Slide 0: Generate Workout */}
            <SlideGenerateWorkout data={data.slide0} isActive={currentStep === 0} onNext={() => handleSlideChange(currentStep + 1)} />

            {/* Slide 1: Sex */}
            <SlideSex
              data={data.slide1}
              isActive={currentStep === 1}
              onSelect={(value) => {
                handleFieldChange("sex", value.toLowerCase());
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 2: Age */}
            <SlideAge
              data={data.slide2}
              isActive={currentStep === 2}
              onSelect={(value) => {
                handleFieldChange("ageRange", value.toLowerCase());
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 3: Fitness Goal */}
            <SlideGoal
              data={data.slide3}
              isActive={currentStep === 3}
              onSelect={(value) => {
                handleFieldChange("fitnessGoal", value.toLowerCase());
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 4: Experience */}
            <SlideExperience
              data={data.slide4}
              isActive={currentStep === 4}
              onSelect={(value) => {
                handleFieldChange("experience", value.toLowerCase());
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 5: Workout Split */}
            <SlideWorkout
              data={data.slide5}
              isActive={currentStep === 5}
              workoutOptions={workoutOptions}
              onSelect={(value) => {
                handleFieldChange("workout", value);
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 6: Time */}
            <SlideTime
              data={data.slide6}
              isActive={currentStep === 6}
              timeOptions={timeOptions}
              onSelect={(value) => {
                handleFieldChange("time", value);
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 7: Intensity */}
            <SlideIntensity
              data={data.slide7}
              isActive={currentStep === 7}
              onSelect={(value) => {
                handleFieldChange("intensity", value.toLowerCase());
                handleSlideChange(currentStep + 1);
              }}
            />

            {/* Slide 8: Results */}
            <SlideResults
              data={data.slide8}
              isActive={currentStep === 8}
              formData={formData}
              options={{
                sex: data.slide1.options,
                fitnessGoal: data.slide3.options,
                experience: data.slide4.options,
                workout: { ...data.slide5.options, ...data.slide5.optionsPart2 },
                intensity: data.slide7.options,
              }}
              onGenerateWorkout={onGenerateWorkout}
              loading={isGenerating}
            />
          </div>
        </div>
        {currentStep !== 0 && (
          <button
            className="back-button"
            type="button"
            disabled={isSliding} // prevent clicks mid-slide
            onClick={() => handleSlideChange(currentStep - 1)}
          >
            <BackIcon />
          </button>
        )}
      </div>
    </section>
  );
};

export default Form;
